/**
 * Sparkline + trend pill per un materiale.
 *
 * Renderizza una mini area chart (~150x40) del `unit_price` nel tempo +
 * un pill colorato con la variazione percentuale e il periodo coperto:
 *   "+12% in 6 mesi"   (up, rosso)
 *   "-4% in 3 mesi"    (down, verde)
 *   "stabile"          (flat, grigio)
 *
 * Da usare in EditMaterialDialog (sezione "Storico prezzi") o in tooltip
 * sulla tabella Materials.
 */
import React from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import {
  useMaterialPriceHistory,
  computePriceTrend,
  type PriceHistoryPoint,
} from '@/hooks/useMaterialPriceHistory';
import { formatNumber } from '@/utils/format';

interface MaterialPriceHistorySparklineProps {
  materialId: string;
  /** Default 150. */
  width?: number | string;
  /** Default 40. */
  height?: number;
  /** Mostra il pill di trend accanto al chart (default true). */
  showTrendPill?: boolean;
}

interface ChartPoint {
  date: string;
  price: number;
}

function toChartPoints(points: PriceHistoryPoint[]): ChartPoint[] {
  return points
    .filter((p) => p.unit_price != null && Number.isFinite(Number(p.unit_price)))
    .map((p) => ({
      date: new Date(p.changed_at).toLocaleDateString('it-IT', { month: 'short', year: '2-digit' }),
      price: Number(p.unit_price),
    }));
}

function formatMonthsLabel(months: number): string {
  if (months < 1.5) return '1 mese';
  if (months < 12) return `${Math.round(months)} mesi`;
  const years = months / 12;
  return years < 1.5 ? '1 anno' : `${Math.round(years)} anni`;
}

const MaterialPriceHistorySparkline: React.FC<MaterialPriceHistorySparklineProps> = ({
  materialId,
  width = 150,
  height = 40,
  showTrendPill = true,
}) => {
  const { data: history = [], isLoading } = useMaterialPriceHistory(materialId);

  if (isLoading) {
    return <span className="text-xs text-muted-foreground italic">…</span>;
  }

  const chartPoints = toChartPoints(history);
  const trend = computePriceTrend(history);

  // Caso: storia troppo corta per un chart sensato → solo pill "nuovo"
  if (chartPoints.length < 2) {
    return (
      <span className="text-xs text-muted-foreground italic">
        Storico non ancora disponibile
      </span>
    );
  }

  // Colore in base al trend (per materiali "up" è cattivo perché costa di più)
  const color =
    trend?.direction === 'up' ? '#dc2626'    // rosso
    : trend?.direction === 'down' ? '#16a34a' // verde
    : '#94a3b8';                              // grigio

  return (
    <div className="flex items-center gap-2">
      <div style={{ width, height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartPoints} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            <defs>
              <linearGradient id={`grad-${materialId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip
              cursor={false}
              content={({ active, payload }) => {
                if (!active || !payload || payload.length === 0) return null;
                const p = payload[0].payload as ChartPoint;
                return (
                  <div className="bg-popover text-popover-foreground rounded border shadow text-xs px-2 py-1">
                    <div className="font-semibold">€ {formatNumber(p.price, 4)}</div>
                    <div className="text-muted-foreground">{p.date}</div>
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke={color}
              strokeWidth={1.5}
              fill={`url(#grad-${materialId})`}
              isAnimationActive={false}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {showTrendPill && trend && (
        <div
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium tabular-nums ${
            trend.direction === 'up'
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : trend.direction === 'down'
                ? 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300'
                : 'bg-muted text-muted-foreground'
          }`}
          title={`Da € ${formatNumber(trend.firstPrice, 4)} a € ${formatNumber(trend.lastPrice, 4)}`}
        >
          {trend.direction === 'up' && <TrendingUp className="h-3 w-3" />}
          {trend.direction === 'down' && <TrendingDown className="h-3 w-3" />}
          {trend.direction === 'flat' && <Minus className="h-3 w-3" />}
          {trend.direction === 'flat'
            ? 'stabile'
            : `${trend.changePct > 0 ? '+' : ''}${formatNumber(trend.changePct, 1)}% in ${formatMonthsLabel(trend.monthsCovered)}`}
        </div>
      )}
    </div>
  );
};

export default MaterialPriceHistorySparkline;
